import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaTrash, FaEdit, FaArrowLeft } from "react-icons/fa";
import Sidebar from '../component/Sidebar';
import toast from "react-hot-toast";
import Pagination from "./Pagination";
import CollapsibleHeader from "../component/CollapsibleHeader";

const EMPLOYMENT_STATUS_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "TraineeProbation", value: "TP" },
  { label: "Trainee", value: "TR" },
  { label: "TEP", value: "TEP" },
  { label: "Probation", value: "PB" },
  { label: "Permanent", value: "P" },
  { label: "PDP", value: "PDP" },
  { label: "PD", value: "PD" },
  { label: "ExEmployee", value: "EX" },
];

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]); // normalized: [{id, name}]
  const [designations, setDesignations] = useState([]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20); 
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");


  const fetchAll = async () => {
    try {
      const [empRes, deptRes, desigRes] = await Promise.all([
        axios.get("/api/employees"),
        axios.get("/api/departments"),
        axios.get("/api/designations"),
      ]);

      setEmployees(empRes.data || []);

      // normalize departments to {id, name}
      const depts = (deptRes.data || [])
        .map((d) => ({
          id:
            d.departmentID ||
            d.deptCode ||
            d.code ||
            d.id ||
            "",
          name:
            d.departmentName ||
            d.deptName ||
            d.name ||
            "",
        }))
        .filter((x) => x.id && x.name);
      setDepartments(depts);

      setDesignations(desigRes.data || []);
    } catch (e) {
      console.error("Fetch error:", e);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

const handleSearchChange = (e) => {
  setSearchTerm(e.target.value.toUpperCase());
  setCurrentPage(1);
};

  const deleteEmployee = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      await axios.delete(`/api/employees/${id}`);
      setEmployees((prev) => prev.filter((e) => e._id !== id));
      toast.success("Employee Deleted successfully");
    } catch (e) {
      console.error(e);
    }
  };

  const filteredEmployees = useMemo(() => {
  const normalizedSearch = searchTerm.trim();

  return employees.filter((e) => {
    const fullName = `${e.firstName || ""} ${e.middleName || ""} ${e.lastName || ""}`.toUpperCase();
    const empId = (e.employeeID || "").toUpperCase();
    const departmentName = (e.departmentName || "").toUpperCase();
    const employmentStatus = (e.employmentStatus || "").toUpperCase();
    const matchesDepartment =
      selectedDepartment === "ALL" || departmentName === selectedDepartment.toUpperCase();
    const matchesStatus =
      selectedStatus === "ALL" || employmentStatus === selectedStatus.toUpperCase();

    if (!normalizedSearch) {
      return matchesDepartment && matchesStatus;
    }

    return (
      matchesDepartment &&
      matchesStatus &&
      (fullName.includes(normalizedSearch) ||
      empId.includes(normalizedSearch) ||
      departmentName.includes(normalizedSearch))
    );
  });
}, [employees, searchTerm, selectedDepartment, selectedStatus]);

const indexOfLast = currentPage * perPage;
const indexOfFirst = indexOfLast - perPage;

// Logic to show all if selected, otherwise slice the array
const paginatedEmployees = perPage === "all" 
  ? filteredEmployees 
  : filteredEmployees.slice(indexOfFirst, indexOfLast);

  return (
    <div className="flex h-screen flex-col md:flex-row overflow-hidden">
    
      <Sidebar />
   
    <div className="page-panel min-w-0 p-2 sm:p-3 md:p-2">
    <div className="page-panel min-w-0 rounded-md bg-white p-2.5 shadow-md sm:p-3">
    <div className="page-sticky-header mb-1 bg-white pb-1">
      <CollapsibleHeader title="Employee List Header" contentClassName="mt-1">
        <div className="page-title-bar p-2.5 sm:p-3">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base font-bold text-dorika-blue sm:text-lg whitespace-nowrap">
                Employee List
              </h2>
              <div className="xl:hidden flex shrink-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-blue-700 text-white shadow hover:bg-blue-800 transition"
                >
                  <FaArrowLeft />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-2 sm:gap-3">
              <div className="field-stack-mobile min-w-[220px] flex-1">
                <label>Search</label>
                <input
                  type="text"
                  placeholder="Search Name or Emp ID"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="h-8 border border-dorika-blue rounded px-3 py-1 text-sm uppercase focus:outline-none"
                />
              </div>

              <div className="field-stack-mobile w-full sm:w-[180px]">
                <label>Department</label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-8 border border-dorika-blue rounded px-3 py-1 text-sm outline-none bg-white font-semibold text-dorika-blue"
                >
                  <option value="ALL">ALL</option>
                  {[...new Set(departments.map((d) => d.name).filter(Boolean))].map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field-stack-mobile w-full sm:w-[170px]">
                <label>Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-8 border border-dorika-blue rounded px-3 py-1 text-sm outline-none bg-white font-semibold text-dorika-blue"
                >
                  {EMPLOYMENT_STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field-stack-mobile w-full sm:w-[110px]">
                <label>Show</label>
                <select
                  value={perPage}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPerPage(val === "all" ? "all" : parseInt(val));
                    setCurrentPage(1);
                  }}
                  className="h-8 border border-dorika-blue rounded px-3 py-1 text-sm outline-none bg-white font-semibold text-dorika-blue"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value="all">ALL</option>
                </select>
              </div>

              <div className="ml-auto flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
                <div className="hidden xl:block shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="h-8 w-[110px] inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white shadow hover:bg-blue-800 transition"
                  >
                    <FaArrowLeft />
                    <span>Back</span>
                  </button>
                </div>
                <button
                  onClick={() => navigate("/EmployeeMaster")}
                  className="h-8 w-full min-w-0 sm:w-auto sm:min-w-[160px] bg-dorika-orange hover:bg-dorika-blue text-white px-4 py-1 rounded font-semibold text-sm whitespace-nowrap"
                >
                  Add Employee
                </button>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleHeader>
    </div>
     <div className="table-scroll-area">
        <table className="table-fixed-header min-w-[700px] w-full table-auto border border-dorika-blue">
        <thead className="bg-dorika-blue text-white text-sm">
          <tr>
            <th className="border border-dorika-blue px-2 py-1">Sl No</th>
            <th className="border border-dorika-blue px-2 py-1">Emp ID</th>
            <th className="border border-dorika-blue px-2 py-1">Status</th>
            <th className="border border-dorika-blue px-2 py-1">Name</th>
            {/* <th className="border border-dorika-blue px-2 py-1">Email</th>*/}
            <th className="border border-dorika-blue px-2 py-1">Department</th> 
            <th className="border border-dorika-blue px-2 py-1">Designation</th>
            <th className="border border-dorika-blue px-2 py-1">Mobile No.</th>
            {/* <th className="border border-dorika-blue px-2 py-1">Employment</th>
            <th className="border border-dorika-blue px-2 py-1">Work Location</th>
            <th className="border border-dorika-blue px-2 py-1">Contact</th>
            <th className="border border-dorika-blue px-2 py-1">Status</th> */}
            <th className="border border-dorika-blue px-2 py-1">Action</th>
          </tr>
        </thead>
        <tbody className="text-xs sm:text-sm text-center">
          {employees.length ? (
           paginatedEmployees.map((e,index) => (
              <tr key={e._id} className="hover:bg-dorika-blueLight transition">
                <td className="border border-dorika-blue px-2 py-1">{perPage === "all" ? index + 1 : (currentPage - 1) * perPage + index + 1}</td>
                <td className="border border-dorika-blue px-2 py-1">{e.employeeID}</td>
                 <td className="border border-dorika-blue px-2 py-1">{e.employmentStatus}</td>
                <td className="border border-dorika-blue px-2 py-1">
                  {e.firstName} {e.middleName} {e.lastName}
                </td>
                {/* <td className="border border-dorika-blue px-2 py-1">{e.email}</td> */}

                {/* Show ONLY department name (from code) */}
                <td className="border border-dorika-blue px-2 py-1">
                  {e.departmentName}
                </td>

                <td className="border border-dorika-blue px-2 py-1">
                  {e.designationName}
                </td>
                <td className="border border-dorika-blue px-2 py-1">{e.permanentAddress.mobile}</td>
                {/* <td className="border border-dorika-blue px-2 py-1">{e.employmentType}</td>
                <td className="border border-dorika-blue px-2 py-1">{e.workLocation}</td>
                <td className="border border-dorika-blue px-2 py-1">{e.contactNo}</td>
                <td className="border border-dorika-blue px-2 py-1">{e.status}</td> */}
                <td className="border border-dorika-blue px-2 py-1">
                  <div className="flex justify-center gap-4 sm:gap-8">
                   <button
                    onClick={() => navigate("/EmployeeMaster", { state: { employee: e, id: e._id } })}
                    className="text-dorika-blue hover:text-dorika-green
"
                  >
                    <FaEdit />
                  </button>

                    <button
                      onClick={() => deleteEmployee(e._id)}
                      className="text-dorika-orange hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" className="text-center py-4 text-gray-500">
                No employees found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {perPage !== "all" && (
        <Pagination
          total={filteredEmployees.length}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
    </div>
    </div>
    </div>
  );
};

export default EmployeeList;
